"use client";

import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { AccountDisabledState } from "@/components/AccountDisabledState/AccountDisabledState";
import { AuthLoadingScreen } from "@/components/AuthLoadingScreen/AuthLoadingScreen";
import { AuthUnavailableState } from "@/components/AuthUnavailableState/AuthUnavailableState";
import { NoOrgAccessState } from "@/components/NoOrgAccessState/NoOrgAccessState";
import { resolveTrimbleDestination } from "@/lib/auth/resolveDestination.client";
import { buildResolveLoginPath } from "@/lib/auth/resolveDestination.shared";
import { getTrimbleAccessToken, linkTrimbleAccount, TrimbleLinkError } from "@/lib/authBackend/trimble/client";
import { ACCOUNT_DISABLED } from "@/lib/authBackend/trimble/linkError";
import { reportAuthFailure } from "@/lib/authBackend/trimble/telemetry";
import { useAuth } from "@/lib/authBackend/trimble/tid/context";

interface TrimbleResolvePageProps {
  returnTo?: string;
  org?: string;
  invited?: boolean;
}

export function TrimbleResolvePage({ returnTo, org, invited }: TrimbleResolvePageProps) {
  const router = useRouter();
  const locale = useLocale();
  const { client, isAuthenticated, isLoading: authLoading } = useAuth();
  const [noAccess, setNoAccess] = useState(false);
  const [authUnavailable, setAuthUnavailable] = useState(false);
  const [accountDisabled, setAccountDisabled] = useState(false);
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (authLoading || resolvedRef.current) return;

    if (invited) {
      // A session already in this tab belongs to whoever used the browser last, not necessarily the invitee.
      resolvedRef.current = true;
      if (isAuthenticated) client.discardSession();
      router.replace(buildResolveLoginPath(locale, returnTo, org, undefined, true));
      return;
    }

    if (!isAuthenticated) {
      // Use a locale-prefixed path so next-intl middleware does not issue a
      // 307 redirect, which would trigger a full page reload and wipe the
      // in-memory Trimble token.
      router.replace(buildResolveLoginPath(locale, returnTo, org));
      return;
    }

    resolvedRef.current = true;

    async function resolve() {
      // Must complete before any other authenticated call — see
      // TrimbleCallbackPage.tsx for why (no email claims on the access_token).
      try {
        await linkTrimbleAccount();
      } catch (err) {
        // Retries exhausted on a 5xx or network failure. A returning user is already
        // linked, so the resolver below still works for them; a genuinely unlinked one
        // gets a 401 there, which it maps to auth_unavailable. Permanent rejections
        // still stop here — they carry a reason the user needs to see.
        if (!(err instanceof TrimbleLinkError && err.retryable)) throw err;
        reportAuthFailure("link", err, { fellThrough: true });
      }
      const token = await getTrimbleAccessToken();

      if (!token) {
        // "reauth_required", not "session_expired" — we don't have positive evidence the
        // access token actually expired (vs. an unrelated refresh failure), so don't
        // claim a specific reason we can't back up.
        router.replace(buildResolveLoginPath(locale, returnTo, org, "reauth_required"));
        return;
      }

      const destination = await resolveTrimbleDestination(token, returnTo, org);
      if (destination.kind === "account_disabled") {
        setAccountDisabled(true);
        return;
      }
      if (destination.kind === "auth_unavailable") {
        setAuthUnavailable(true);
        return;
      }
      if (destination.kind === "no_access") {
        setNoAccess(true);
        return;
      }

      router.replace(`/${locale}${destination.path}`);
    }

    void resolve().catch((err) => {
      if (err instanceof TrimbleLinkError && err.code === ACCOUNT_DISABLED) {
        setAccountDisabled(true);
        return;
      }
      // A non-retryable TrimbleLinkError is a permanent business-rule rejection — a
      // different code so /login shows the right copy. Everything else here (a
      // retryable TrimbleLinkError that exhausted its retries, or any other unexpected
      // failure) isn't a confirmed expiry either, so "reauth_required", not
      // "session_expired".
      const errorCode = err instanceof TrimbleLinkError && !err.retryable ? "link_failed" : "reauth_required";
      router.replace(buildResolveLoginPath(locale, returnTo, org, errorCode));
    });
  }, [authLoading, client, isAuthenticated, locale, returnTo, org, invited, router]);

  if (accountDisabled) {
    return <AccountDisabledState />;
  }

  if (authUnavailable) {
    return <AuthUnavailableState />;
  }

  if (noAccess) {
    return <NoOrgAccessState />;
  }

  return <AuthLoadingScreen />;
}
